/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { BOOKING_STATUS, INVOICE_STATUS } from "../constants/constants";

export const useCartItemStatus = ({ invoiceData = null, initialStatus = null }) => {
  const computedStatus = useMemo(() => {
    const finalStatuses = [BOOKING_STATUS.CANCELLED, BOOKING_STATUS.ERROR];

    if (initialStatus && finalStatuses.includes(initialStatus)) {
      return initialStatus;
    }

    const invoiceStatus = invoiceData?.status;
    const paymentProcessor = invoiceData?.payment_processor;
    const paymentReceived = invoiceData?.payment_received;

    if (invoiceData) {
      if (paymentProcessor === "delay" && !paymentReceived) {
        return BOOKING_STATUS.DELAY;
      }

      if (invoiceStatus === INVOICE_STATUS.PAID) {
        if (paymentReceived) {
          return BOOKING_STATUS.CONFIRMED;
        }
        return BOOKING_STATUS.CONFIRMED;
      }

      switch (invoiceStatus) {
        case INVOICE_STATUS.PENDING:
        case INVOICE_STATUS.NEW:
          return BOOKING_STATUS.PENDING_PAYMENT;
        case INVOICE_STATUS.ERROR:
          return BOOKING_STATUS.ERROR;
        case INVOICE_STATUS.CANCELLED:
        case INVOICE_STATUS.CANCELLED_BY_TIMEOUT:
          return BOOKING_STATUS.CANCELLED;
        default:
          break;
      }
    }

    return initialStatus || BOOKING_STATUS.PENDING;
  }, [invoiceData, initialStatus]);

  const statusMetadata = useMemo(() => {
    const metadata = {
      [BOOKING_STATUS.CONFIRMED]: {
        showOrderDetails: true,
      },
      [BOOKING_STATUS.PENDING]: {
        showOrderDetails: false,
      },
      [BOOKING_STATUS.PENDING_DELAY]: {
        showOrderDetails: true,
      },
      [BOOKING_STATUS.PENDING_PAYMENT]: {
        showOrderDetails: false,
      },
      [BOOKING_STATUS.DELAY]: {
        showOrderDetails: true,
      },
      [BOOKING_STATUS.CANCELLED]: {
        showOrderDetails: false,
      },
      [BOOKING_STATUS.ERROR]: {
        showOrderDetails: false,
      },
    };

    return metadata[computedStatus] || metadata[BOOKING_STATUS.PENDING];
  }, [computedStatus]);

  return {
    status: computedStatus,
    metadata: statusMetadata,
    isConfirmed: computedStatus === BOOKING_STATUS.CONFIRMED,
    isPending: computedStatus === BOOKING_STATUS.PENDING || computedStatus === BOOKING_STATUS.PENDING_PAYMENT,
    isCancelled: computedStatus === BOOKING_STATUS.CANCELLED,
    isError: computedStatus === BOOKING_STATUS.ERROR,
    isDelay: computedStatus === BOOKING_STATUS.DELAY,
    shouldShowOrderDetails: statusMetadata.showOrderDetails,
  };
};

export default useCartItemStatus;